import { DiscountFactory } from '../factories/discount-factory.js';
import { Discount } from '../models/serializable/discounts/discount.js';
import { EventPromoCode } from '../models/serializable/discounts/event-promo-code.js';
import { Price } from '../models/serializable/price.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../utility/null-safety.js';
import { DiscountModifier, DiscountProvider } from './discount-provider.js';

export class EventPromoCodeDiscountProvider extends DiscountProvider {
  private uses: number;

  constructor(private readonly promoCode: EventPromoCode) {
    super();
    this.uses = this.promoCode.uses ?? 0;
  }

  findDiscountForSingleTicket(ticketId: string): Discount | null {
    if(!this.doesPromoCodeApplyToTicket(ticketId)) {
      return null;
    }
    if(this.promoCode.maxUses != null && this.uses >= this.promoCode.maxUses) {
      return null;
    }

    return this._findDiscountForSingleTicket();
  }

  findDiscountForMultipleTickets(ticketId: string, quantity: number): DiscountModifier | null {
    if(!this.doesPromoCodeApplyToTicket(ticketId)) {
      return null;
    }
    if(this.promoCode.maxUses != null && this.uses >= this.promoCode.maxUses) {
      return null;
    }

    if(isNotNullOrUndefined(quantity) && quantity > 1) {
      return this._findDiscountForMultipleTickets(quantity, this.promoCode.maxUses);
    }
    else {
      const discount = this._findDiscountForSingleTicket();
      return { discount, availableUses: 1 };
    }
  }

  findDiscountForOrder(orderPrice: Price): Discount | null {
    return null;
  }

  reset(): void {
    this.uses = this.promoCode.uses ?? 0;
  }

  private doesPromoCodeApplyToTicket(ticketId: string): boolean {
    const validTicketIds = this.promoCode.ticketId;
    return (isNullOrUndefined(validTicketIds) || validTicketIds.includes(ticketId));
  }

  private _findDiscountForSingleTicket(): Discount {
    this.uses++;
    return DiscountFactory.fromEventPromoCode(this.promoCode);
  }

  private _findDiscountForMultipleTickets(quantity: number, maxUses: number | null): DiscountModifier {
    const discount = DiscountFactory.fromEventPromoCode(this.promoCode);
    const availableUses = (maxUses == null) ? quantity : Math.min(quantity, maxUses - this.uses);

    this.uses += availableUses;

    return { discount, availableUses };
  }
}
